import React, { FC, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { RichTextContent } from "../../Unknown/RichTextContent";
import Typography from "../../Unknown/Typography";
import IconButton from "../../Unknown/IconButton";
import Button from "../../Unknown/Button";
import Box from "../../Unknown/Box";
import { CloseIcon } from "../../Unknown/Icons";
import { ConfirmedAppointment } from "../AppointmentConfirmedScreen/parseConfirmedAppointment";
import useTranslations from "./useTranslations";
import messages from "./messages";

type SuggestedPackageItemProps = {
  packageName: string;
  oldPrice: number;
  marketingText: string;
  appointmentData: ConfirmedAppointment;
  offeringIds: string[];
  isSelected: boolean;
  isDisabled: boolean;
  priceDiff: number;
  onSelect: () => void;
  onDeselect: () => void;
};

const SuggestedPackageItem: FC<SuggestedPackageItemProps> = ({
  packageName,
  oldPrice,
  marketingText,
  offeringIds,
  appointmentData,
  priceDiff,
  isSelected,
  isDisabled,
  onSelect,
  onDeselect,
}) => {
  const {
    translations: { upgradeLabel, andLabel },
  } = useTranslations();

  const { formatNumber, formatMessage } = useIntl();

  const offerings = useMemo(
    () =>
      appointmentData.offerings.filter((appointmentOffering) =>
        offeringIds.includes(appointmentOffering.globalOfferingId),
      ),
    [appointmentData.offerings, offeringIds],
  );

  const title = useMemo(() => {
    const offeringsTitle = offerings
      .map((offering) => offering.name)
      .join(andLabel);

    return formatMessage(messages.upgradePackageLabel, {
      offeringNames: offeringsTitle,
      packageName,
    });
  }, [offerings, packageName, formatMessage, andLabel]);

  const getPriceLabel = useCallback(() => {
    const formattedPrice = formatNumber(oldPrice, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    const formattedOldPrice = (
      <Typography
        component="span"
        color="error.main"
        sx={{ textDecoration: "line-through" }}
      >
        {formattedPrice}
      </Typography>
    );

    return formatMessage(messages.moreLabel, {
      oldPrice: formattedOldPrice,
      diff: formatNumber(Math.abs(priceDiff), {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    });
  }, [formatNumber, oldPrice, formatMessage, priceDiff]);

  return (
    <Box
      mt={2}
      p={3.25}
      border={1}
      borderColor="divider"
      borderRadius={1}
      bgcolor={isDisabled ? "#EEEEEE" : "transparent"}
    >
      <Box
        display="flex"
        gap={{ xs: 4, md: 8 }}
        justifyContent={{ xs: "center", md: "space-between" }}
        flexDirection={{ xs: "column", md: "row" }}
      >
        <Box flexGrow={1} maxWidth={{ xs: "100%", md: "50%" }}>
          <Typography mb={0.5}>{title}</Typography>
          <RichTextContent content={marketingText} />
        </Box>
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          gap={{ xs: 2, md: 8 }}
        >
          <Box>
            <Typography py={2} whiteSpace={{ xs: "normal", md: "nowrap" }}>
              {getPriceLabel()}
            </Typography>
          </Box>
          <Box width={{ xs: "100%", md: "fit-content" }}>
            {isSelected ? (
              <IconButton
                sx={{
                  p: 1,
                  "& svg": {
                    width: 35,
                    height: 35,
                  },
                }}
                color="error"
                onClick={onDeselect}
              >
                <CloseIcon />
              </IconButton>
            ) : (
              <Button
                size="large"
                variant="contained"
                color="success"
                disabled={isDisabled}
                onClick={onSelect}
              >
                {upgradeLabel}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SuggestedPackageItem;
